/**
 * PlatformConfigEditor — full editor for canonical Platform schema fields.
 *
 * Renders ALL Platform fields from openapi-content-plan.yaml (1.3.1+):
 *   • displayName              (TextField)
 *   • profileUrl               (TextField URL — controls Scrape more button)
 *   • isActive                 (Switch — soft delete)
 *   • maxPostLength            (TextField number)
 *   • optimalSlots[]           (weekly slot rows of {day, timeLocal, score} + add/remove)
 *   • linkPenalty              (Select [reduces_reach, neutral])
 *   • linkPlacementRule        (Select [in_body, first_comment, bio_only])
 *   • contentLifespanHours     (TextField number)
 *   • hasAlgorithm             (Switch)
 *   • icon                     (TextField — emoji or short identifier)
 *
 * Rendered as a tab inside PlatformsSection (one tab per platform).
 * Saves via authorPlatformService.updatePlatform with stripUndefined.
 */

import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import {
  getAuthorPlatformService,
  type AuthorBasePath,
} from '../../services/AuthorPlatformService';
import type { Platform } from '@segmently/schema-models/content-plan';
import type { AuthorPlatformLite } from '../../hooks/useAuthorPlatforms';
import {
  PlatformCadenceFields,
} from '@modules/content-plan/components/platforms/PlatformCadenceFields';
import {
  MAX_CADENCE_SLOTS_PER_WEEK,
  cadenceFieldSlotsToPlatformOptimalSlots,
  resolvePlatformOptimalFieldSlots,
  type CadenceFieldSlot,
} from '@modules/content-plan/utils/strategyCadence';

const TESTID = 'platform-config-editor';

export interface PlatformConfigEditorProps {
  projectId: string;
  authorId: string;
  platform: AuthorPlatformLite;
  /** Discriminates self-author vs tracked creator. Default `'authors'`. */
  basePath?: AuthorBasePath;
}

export const PlatformConfigEditor: React.FC<PlatformConfigEditorProps> = ({
  projectId,
  authorId,
  platform,
  basePath = 'authors',
}) => {
  const platformService = getAuthorPlatformService(basePath);
  const [draft, setDraft] = useState<Partial<Platform>>({});
  const [optimalTimes, setOptimalTimes] = useState<CadenceFieldSlot[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedFlash, setSavedFlash] = useState(false);

  // Hydrate draft from incoming platform doc
  useEffect(() => {
    setDraft({
      displayName: platform.displayName ?? platform.id,
      profileUrl: platform.profileUrl,
      icon: (platform as any).icon,
      isActive: platform.isActive ?? true,
      hasAlgorithm: platform.hasAlgorithm,
      maxPostLength: platform.maxPostLength,
      contentLifespanHours: (platform as any).contentLifespanHours,
      linkPenalty: (platform as any).linkPenalty,
      linkPlacementRule: (platform as any).linkPlacementRule,
      optimalPostFrequency: platform.optimalPostFrequency,
    } as Partial<Platform>);
    setOptimalTimes(resolvePlatformOptimalFieldSlots(platform).slice(0, MAX_CADENCE_SLOTS_PER_WEEK));
  }, [platform]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await platformService.updatePlatform({
        projectId,
        authorId,
        platformId: platform.id,
        patch: {
          ...draft,
          optimalPostFrequency: optimalTimes.length > 0
            ? { min: optimalTimes.length, max: optimalTimes.length }
            : undefined,
          optimalSlots: optimalTimes.length > 0
            ? cadenceFieldSlotsToPlatformOptimalSlots(optimalTimes)
            : undefined,
        } as any,
      });
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2000);
    } catch (e: any) {
      setError(e?.message ?? 'Failed to save platform');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 3 }} data-testid={TESTID}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h6">Config — {platform.displayName ?? platform.id}</Typography>
          <Typography variant="caption" color="text.secondary">
            Platform-level configuration. Per-platform — independent of voice
            and corpus.
          </Typography>
        </Box>
        {savedFlash && (
          <Chip size="small" color="success" label="Saved" />
        )}
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Stack spacing={2}>
        {/* Identity row: displayName + icon + isActive */}
        <Stack direction="row" spacing={2} alignItems="flex-start">
          <TextField
            label="Display name"
            size="small"
            fullWidth
            value={draft.displayName ?? ''}
            onChange={(e) => setDraft({ ...draft, displayName: e.target.value })}
            inputProps={{ 'data-testid': `${TESTID}-displayName` }}
          />
          <TextField
            label="Icon"
            size="small"
            sx={{ width: 100 }}
            value={(draft as any).icon ?? ''}
            onChange={(e) => setDraft({ ...draft, icon: e.target.value } as any)}
            inputProps={{ 'data-testid': `${TESTID}-icon` }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={draft.isActive ?? true}
                onChange={(e) => setDraft({ ...draft, isActive: e.target.checked })}
                data-testid={`${TESTID}-isActive`}
              />
            }
            label={draft.isActive ? 'Active' : 'Disabled'}
          />
        </Stack>

        {/* profileUrl — drives Scrape more button */}
        <TextField
          label="Profile URL"
          size="small"
          fullWidth
          value={draft.profileUrl ?? ''}
          onChange={(e) => setDraft({ ...draft, profileUrl: e.target.value })}
          helperText={`Author's URL on this platform (e.g. https://www.linkedin.com/in/handle/). Used by Scrape more.`}
          inputProps={{ 'data-testid': `${TESTID}-profileUrl` }}
        />

        {/* Numeric stats: maxPostLength + contentLifespanHours + hasAlgorithm */}
        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            label="Max post length"
            size="small"
            type="number"
            sx={{ width: 160 }}
            value={draft.maxPostLength ?? ''}
            onChange={(e) =>
              setDraft({
                ...draft,
                maxPostLength: e.target.value ? Number(e.target.value) : undefined,
              })
            }
            inputProps={{ min: 0, 'data-testid': `${TESTID}-maxPostLength` }}
            helperText="Chars (0 = unlimited)"
          />
          <TextField
            label="Content lifespan (hours)"
            size="small"
            type="number"
            sx={{ width: 200 }}
            value={(draft as any).contentLifespanHours ?? ''}
            onChange={(e) =>
              setDraft({
                ...draft,
                contentLifespanHours: e.target.value ? Number(e.target.value) : undefined,
              } as any)
            }
            inputProps={{ min: 0, 'data-testid': `${TESTID}-contentLifespanHours` }}
            helperText="Until algo decay"
          />
          <FormControlLabel
            control={
              <Switch
                checked={Boolean(draft.hasAlgorithm)}
                onChange={(e) => setDraft({ ...draft, hasAlgorithm: e.target.checked })}
                data-testid={`${TESTID}-hasAlgorithm`}
              />
            }
            label="Algorithmic feed"
          />
        </Stack>

        <PlatformCadenceFields
          mode="author"
          slots={optimalTimes}
          onSlotsChange={setOptimalTimes}
          testIdPrefix={TESTID}
        />

        {/* Link policy: linkPenalty + linkPlacementRule */}
        <Stack direction="row" spacing={2}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" color="text.secondary">Link penalty</Typography>
            <Select
              size="small"
              fullWidth
              value={(draft as any).linkPenalty ?? ''}
              onChange={(e) => setDraft({ ...draft, linkPenalty: e.target.value as any } as any)}
              displayEmpty
              data-testid={`${TESTID}-linkPenalty`}
            >
              <MenuItem value=""><em>Not set</em></MenuItem>
              <MenuItem value="reduces_reach">reduces_reach</MenuItem>
              <MenuItem value="neutral">neutral</MenuItem>
            </Select>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" color="text.secondary">Link placement</Typography>
            <Select
              size="small"
              fullWidth
              value={(draft as any).linkPlacementRule ?? ''}
              onChange={(e) =>
                setDraft({ ...draft, linkPlacementRule: e.target.value as any } as any)
              }
              displayEmpty
              data-testid={`${TESTID}-linkPlacementRule`}
            >
              <MenuItem value=""><em>Not set</em></MenuItem>
              <MenuItem value="in_body">in_body</MenuItem>
              <MenuItem value="first_comment">first_comment</MenuItem>
              <MenuItem value="bio_only">bio_only</MenuItem>
            </Select>
          </Box>
        </Stack>
        {/* Save */}
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={14} /> : <SaveIcon />}
            disabled={saving}
            onClick={handleSave}
            data-testid={`${TESTID}-saveBtn`}
          >
            {saving ? 'Saving…' : 'Save config'}
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
};

export default PlatformConfigEditor;
